/**
 * The 3D-prerendered character system ("c3d") — catalog + frame geometry.
 * Characters are modular 3D models rendered to 8-direction sprite sheets through
 * the pixelation pipeline (scripts/style/blender-layer-render.py + pixelate +
 * pack-c3d). One sheet per body/eye variant; hair styles arrive as additional
 * aligned sheets and colourways are runtime palette remaps.
 *
 * Sheet layout (see pack-c3d.mjs): 144px cells, 8 rows = yaw directions
 * (d0 front, 45° CCW steps: d2 = left, d4 = back, d6 = right);
 * cols 0-7 = idle frames (6 fps), cols 8-19 = walk frames (12 fps).
 */
export const C3D_CELL = 144;
export const C3D_DIRS = 8;
export const C3D_IDLE_FRAMES = 8;
export const C3D_WALK_FRAMES = 12;
export const C3D_RUN_FRAMES = 12;
/** Atlas columns per row: idle (0-7) + walk (8-19) + run (20-31). Layer sheets without run
 *  frames are only 20 cols; the compositor fills their run region from their walk frames. */
export const C3D_COLS = C3D_IDLE_FRAMES + C3D_WALK_FRAMES + C3D_RUN_FRAMES;
export const C3D_WALK_SHEET_COLS = C3D_IDLE_FRAMES + C3D_WALK_FRAMES; // 20: a sheet with no run
export const C3D_IDLE_MS = 1000 / 6;
export const C3D_WALK_MS = 1000 / 12;
export const C3D_RUN_MS = 1000 / 15; // run cycles a touch faster than walk
/** Attack: a STANDALONE sheet (not in the atlas) of 16 frames x 8 dir-rows, with the sword + shield
 *  baked in. Played once per swing over ~0.9s, row chosen like the body. Its cell is LARGER than the
 *  144 body cell so the swung sword/shield aren't cropped; the figure is the same size (rendered at a
 *  proportionally wider ortho), just centered with margin. The billboard scales the attack quad by
 *  C3D_ATTACK_CELL/C3D_CELL while playing, so the figure stays the same on-screen size + foot height. */
export const C3D_ATTACK_CELL = 216; // 1.5x the 144 body cell — room for the sword swing
export const C3D_ATTACK_FRAMES = 16;
export const C3D_ATTACK_COLS = 16;
export const C3D_ATTACK_MS = 1000 / 18;
export const C3D_ATTACK_SHEET = "/assets/c3d/attack_sword.png";
/** Smoke: a looping idle override (standing + smoking a cigarette) while a Blunt buff is active.
 *  8 frames x 8 dir-rows at the normal 144 body cell, played at the idle 6fps. Built at runtime per
 *  appearance (composited idle frames + a cigarette/smoke overlay) so clothing/hair/skin are kept. */
export const C3D_SMOKE_FRAMES = 8;
export const C3D_SMOKE_COLS = 8;
export const C3D_SMOKE_MS = 1000 / 6;
/** Sit: a STATIC seated pose (1 frame x 8 dir-rows at the 144 body cell), modelled on the block
 *  pose — standalone per-layer sheets `sit_<layer>.png` composited at runtime. Used while a player is
 *  seated at a casino table. 1 column → no frame helper needed (the billboard sets the row offset). */
export const C3D_SIT_COLS = 1;
export const C3D_SIT_SHEET = "/assets/c3d/sit_body.png";
/** Jump: a STATIC mid-air pose (1 frame x 8 dir-rows at the 144 body cell), modelled on the block/sit
 *  pose — standalone per-layer sheets `jump_<layer>.png` composited at runtime. Shown while a player is
 *  airborne (Space). Head/arms stay at the idle pose, so the head-attached layers (hair/hat/tops/weapon)
 *  reuse the idle frame-0 art; only body + legs are jump-posed. */
export const C3D_JUMP_COLS = 1;
export const C3D_JUMP_SHEET = "/assets/c3d/jump_body.png";
/** In-world display scale: 144px sprites shown at ~50% ≈ 3 tiles tall. */
export const C3D_WORLD_SCALE = 0.5;
/** 8-way facing -> sheet row. The render pivot rotates the model CCW (top view),
 *  which turns its face toward screen-right first: d1 = down-right ... d6 = left. */
export const C3D_DIR_ROW = {
    "down": 0,
    "down-right": 1,
    "right": 2,
    "up-right": 3,
    "up": 4,
    "up-left": 5,
    "left": 6,
    "down-left": 7,
};
export function c3dIdleFrame(dir, f) {
    return (C3D_DIR_ROW[dir] ?? 0) * C3D_COLS + (f % C3D_IDLE_FRAMES);
}
export function c3dWalkFrame(dir, f) {
    return (C3D_DIR_ROW[dir] ?? 0) * C3D_COLS + C3D_IDLE_FRAMES + (f % C3D_WALK_FRAMES);
}
export function c3dRunFrame(dir, f) {
    return (C3D_DIR_ROW[dir] ?? 0) * C3D_COLS + C3D_IDLE_FRAMES + C3D_WALK_FRAMES + (f % C3D_RUN_FRAMES);
}
/** Eye/face variants — each is a full body sheet + portrait pair on disk. */
export const C3D_EYES = {
    0: { code: "none", label: "Round" },
    1: { code: "flat", label: "- -" },
};
/**
 * Hair styles: empty until the image-conditioned hair batch lands. Each entry will
 * point at an aligned hair sheet (same layout as the body sheet) rendered from the
 * styled character; colourways come from HAIR_COLORS ramp remaps at load time.
 */
/** Hair styles. `offsetY` nudges the layer down (+) / up (−) in 144px cell pixels,
 *  tuned per style via the creator debug panel then baked here. */
export const C3D_HAIR = {
    1: { code: "long", label: "Long", offsetY: -1 }, // net up 3px from original +2
    2: { code: "bob", label: "Bob", offsetY: -3 }, // net up 3px from original 0
    3: { code: "crop", label: "Crop", offsetY: -3 },
    4: { code: "spiky", label: "Spiky", offsetY: -3 },
};
/** Per-style hair vertical offset in 144px cell pixels (0 if unset). */
export function c3dHairOffsetY(style) {
    return C3D_HAIR[style]?.offsetY ?? 0;
}
/** Hair colourways (applied as palette ramp remaps on the brown master renders). */
export const C3D_HAIR_COLORS = [
    { name: "Brown", ramp: [[105, 68, 44], [140, 92, 58], [177, 124, 79]] },
    { name: "Black", ramp: [[24, 22, 26], [45, 42, 48], [72, 68, 76]] },
    { name: "White", ramp: [[210, 208, 212], [232, 231, 235], [248, 248, 250]] },
    { name: "Gray", ramp: [[110, 110, 116], [148, 148, 154], [186, 186, 192]] },
    { name: "Blonde", ramp: [[188, 145, 64], [224, 186, 98], [246, 222, 148]] },
    { name: "Red", ramp: [[142, 40, 30], [186, 64, 44], [222, 100, 70]] },
    { name: "Pink", ramp: [[214, 110, 158], [238, 150, 190], [250, 192, 216]] },
    { name: "Purple", ramp: [[110, 70, 160], [148, 104, 198], [186, 148, 228]] },
    { name: "Blue", ramp: [[58, 92, 170], [92, 134, 210], [138, 178, 240]] },
    { name: "Green", ramp: [[58, 122, 78], [90, 160, 108], [134, 198, 146]] },
];
/**
 * Selectable skin tones for the c3d body. Index 0 = the body as rendered (no remap,
 * a warm medium). 1-5 remap the body's skin pixels by luminance onto a [shadow, mid,
 * light] ramp (warm hues, kept stepped). Clothes (near-white/grey) and eyes (dark,
 * desaturated) are not warm-skin pixels, so they're left untouched. Names are shown
 * in the creator's Skin row.
 */
export const C3D_SKIN_TONES = [
    { name: "1", ramp: null }, // 0 = body as rendered
    { name: "2", ramp: [[196, 152, 120], [230, 194, 162], [250, 228, 204]] },
    { name: "3", ramp: [[178, 130, 96], [216, 172, 134], [244, 210, 176]] },
    { name: "4", ramp: [[140, 96, 64], [188, 138, 96], [222, 176, 132]] },
    { name: "5", ramp: [[98, 64, 42], [144, 98, 64], [184, 132, 94]] },
    { name: "6", ramp: [[64, 40, 26], [100, 66, 44], [138, 96, 66]] },
];
/** Skin-tone ramp for the given index, or null to leave the body as rendered. */
export function c3dSkinRamp(tone) {
    return C3D_SKIN_TONES[tone]?.ramp ?? null;
}
export function c3dBodySheet(eyes) {
    const v = C3D_EYES[eyes]?.code ?? "none";
    return `c3d/body_${v}.png`;
}
export function c3dPortrait(eyes) {
    const v = C3D_EYES[eyes]?.code ?? "none";
    return `c3d/portrait_${v}.png`;
}
/** Hair-layer sheet path for a style (0 = none/bald). */
export function c3dHairSheet(style) {
    const h = C3D_HAIR[style];
    return h ? `c3d/hair_${h.code}.png` : null;
}
/**
 * Equippable CLOTHING layers — Blender-rendered like hair (one aligned sheet per style,
 * neutral white), composited on the body and recoloured by luminance at load time.
 * Each style 0 = none. `offsetY` nudges the layer in 144px cell pixels if needed.
 */
// `combat: true` ⇒ this top has attack_/block_ combat sheets rendered; the rest only have the
// idle/walk sheet, so the shirt is simply omitted during the swing/block stance (and we skip the
// request to avoid 404s). See c3dTopHasCombatSheet.
export const C3D_TOP = {
    1: { code: "tee", label: "Tee", combat: true },
    2: { code: "ghosttee", label: "Ghost Tee", combat: true }, // pre-coloured (black + white ghost), colour 0
    3: { code: "circle", label: "Circle Tee" }, // recolourable: tonal ring on the chest
    4: { code: "tech", label: "Tech Top" }, // recolourable; matches "techpants" (Nike-tech set)
    5: { code: "coin", label: "Coin Tee" }, // pre-coloured (black + gold coin), colour 0
    6: { code: "suit", label: "Suit" }, // pre-coloured (black formal suit), colour 0
    7: { code: "winston", label: "Barkeep Shirt" }, // pre-coloured (white + black buttons), colour 0
    8: { code: "pickaxe", label: "Pickaxe Tee", combat: true }, // pre-coloured (white tee + a pickaxe chest print), colour 0
    9: { code: "bomber", label: "DRKLRD Bomber Jacket", combat: true }, // pre-coloured (black lowpoly jacket + silver zip), colour 0
    10: { code: "parka", label: "Gray Hooded Parka" }, // pre-coloured (gray fabric + zipper, hood draped on the back), colour 0
    11: { code: "fishvest", label: "Fisherman Vest" }, // pre-coloured (khaki shirt + olive vest & pockets), colour 0 — PREVIEW: base sheets only until approved
};
export const C3D_BOTTOM = {
    1: { code: "jeans", label: "Jeans" },
    // pre-coloured (black + blue underwear + belt), colour 0; baggy legs drape OVER the shoes.
    2: { code: "workpants", label: "Workpants", overShoes: true },
    3: { code: "techpants", label: "Tech Pants" }, // side-stripe joggers, matches the Tech Top
    4: { code: "fishpants", label: "Fisherman Waders" }, // pre-coloured (olive), colour 0 — PREVIEW: base sheets only until approved
};
/** Does this bottom style drape over the shoes (drawn after them) vs tuck behind? */
export function c3dBottomOverShoes(style) {
    return C3D_BOTTOM[style]?.overShoes ?? false;
}
export const C3D_SHOES = {
    1: { code: "sneaker", label: "Sneaker" },
    2: { code: "boots", label: "Fisherman Boots" }, // pre-coloured (dark leather, tall shaft), colour 0 — PREVIEW: base sheets only until approved
};
export const C3D_HAT = {
    1: { code: "cap", label: "Cap" },
    2: { code: "hood", label: "Gray Hood" }, // pre-coloured (gray fabric, worn up), colour 0 — Sal's hood
    3: { code: "fisher", label: "Fisherman Hat" }, // pre-coloured (khaki bucket hat), colour 0 — PREVIEW: base sheets only until approved
    4: { code: "fishbucket", label: "Fishing Hat" }, // pre-coloured khaki bucket hat; +fishing (hat_fishbucket.png)
};
/** MASK styles (a head layer between hair and hat — balaclava, future face masks). `hideHair`
 *  suppresses the hair layer entirely while worn (a balaclava wraps the whole head). */
export const C3D_MASK = {
    1: { code: "balaclava", label: "Balaclava", hideHair: true }, // pre-coloured (charcoal knit), colour 0
};
/** BACK accessory styles (rendered behind the body — capes, backpacks). Empty until the first
 *  back asset lands; the slot infrastructure (equip/appearance/UI) is already wired. */
export const C3D_BACK = {};
/** Garment colourways (luminance ramp remaps on the white master renders). Index 0 =
 *  the neutral white as rendered. ITEM_EQUIP.colorId indexes this list. */
export const C3D_GARMENT_COLORS = [
    { name: "White", ramp: [[200, 200, 205], [228, 228, 232], [250, 250, 252]] }, // 0
    { name: "Black", ramp: [[20, 20, 24], [42, 42, 48], [74, 74, 82]] }, // 1
    { name: "Blue", ramp: [[36, 54, 110], [58, 90, 165], [108, 150, 222]] }, // 2 (jeans)
    // Appended (indices stay stable) — expands NPC/player wardrobe variety. "Leather" is a
    // warm brown used for the bartender's shoes; the rest add adventurer colourways.
    { name: "Leather", ramp: [[70, 44, 26], [110, 72, 42], [150, 104, 66]] }, // 3
    { name: "Tan", ramp: [[150, 120, 80], [186, 156, 110], [216, 194, 150]] }, // 4
    { name: "Green", ramp: [[52, 78, 46], [86, 120, 70], [128, 168, 104]] }, // 5
    { name: "Red", ramp: [[112, 32, 34], [158, 54, 52], [202, 92, 86]] }, // 6
    { name: "Purple", ramp: [[78, 52, 110], [112, 80, 158], [152, 120, 202]] }, // 7
    { name: "Dark Leather", ramp: [[48, 30, 18], [80, 52, 30], [112, 78, 48]] }, // 8 (a second leather brown)
    // Deep browns for ARMORED leather — distinctly DARKER than the leather/tan/dark-leather above
    // (3 shades so the 3 stat variants keep unique colorIds for equipToItem).
    { name: "Deep Leather A", ramp: [[34, 20, 10], [56, 34, 18], [82, 52, 30]] }, // 9
    { name: "Deep Leather B", ramp: [[28, 16, 8], [48, 28, 15], [72, 44, 25]] }, // 10
    { name: "Deep Leather C", ramp: [[40, 24, 13], [62, 38, 21], [90, 58, 34]] }, // 11
];
export function c3dTopSheet(style) {
    const x = C3D_TOP[style];
    return x ? `c3d/top_${x.code}.png` : null;
}
/** Does this top have attack/block combat sheets rendered? Only some do; for the rest the attack/block
 *  compositors skip the shirt layer entirely (rather than requesting a sheet that 404s). */
export function c3dTopHasCombatSheet(style) {
    return C3D_TOP[style]?.combat ?? false;
}
export function c3dBottomSheet(style) {
    const x = C3D_BOTTOM[style];
    return x ? `c3d/bottom_${x.code}.png` : null;
}
export function c3dShoesSheet(style) {
    const x = C3D_SHOES[style];
    return x ? `c3d/shoes_${x.code}.png` : null;
}
export function c3dHatSheet(style) {
    const x = C3D_HAT[style];
    return x ? `c3d/hat_${x.code}.png` : null;
}
export function c3dMaskSheet(style) {
    const x = C3D_MASK[style];
    return x ? `c3d/mask_${x.code}.png` : null;
}
/** Does the worn mask hide the hair layer entirely (e.g. a balaclava)? */
export function c3dMaskHidesHair(style) {
    return C3D_MASK[style]?.hideHair ?? false;
}
export function c3dBackSheet(style) {
    const x = C3D_BACK[style];
    return x ? `c3d/back_${x.code}.png` : null;
}
export function c3dTopOffsetY(style) { return C3D_TOP[style]?.offsetY ?? 0; }
export function c3dBottomOffsetY(style) { return C3D_BOTTOM[style]?.offsetY ?? 0; }
export function c3dShoesOffsetY(style) { return C3D_SHOES[style]?.offsetY ?? 0; }
export function c3dHatOffsetY(style) { return C3D_HAT[style]?.offsetY ?? 0; }
//# sourceMappingURL=c3d.js.map